import {
  ListMetricsRequest_AggregationType,
  ListMetricsRequest_TargetSchema,
  ListMetricsRequest_Target,
  ListMetricsResponseSchema,
} from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/aqora_service_pb';
import { create } from '@bufbuild/protobuf';
import { queryOptions } from '@tanstack/react-query';
import {
  ListSimulationMetricsRequest_AggregationType,
  ListSimulationMetricsRequest_Target,
} from '@wavingroup/aqora-v2-crate/wavin/simulations/v1/simulations_service_pb';
import { MetricType } from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/metrics_pb';
import { timestampFromDate } from '@bufbuild/protobuf/wkt';
import {
  listMetrics,
  listSimulationMetrics,
} from '~/services/api/fetchers/metrics.api';
import { ChartMetricModel } from '~/shared/models/metrics/ChartMetricModel';
import { MetricsModel } from '~/shared/models/metrics/MetricsModel';
import { SystemModeMetricModel } from '~/shared/models/metrics/SystemModeMetricModel';
import { WaterLevelsModel } from '~/shared/models/metrics/waterLevelsModel/WaterLevelsModel';
import { SimulationModel } from '~/shared/models/system/SimulationModel';

export const getLastFiveYearsRange = () => {
  const now = new Date();
  const pastFiveYears = new Date(now.setFullYear(now.getFullYear() - 5));

  return {
    rangeFrom: timestampFromDate(pastFiveYears),
    rangeTo: timestampFromDate(new Date()),
  };
};

export const getLastWeekRange = () => {
  const now = new Date();
  const pastWeek = new Date(now.setDate(now.getDate() - 7));

  return {
    rangeFrom: timestampFromDate(pastWeek),
    rangeTo: timestampFromDate(new Date()),
  };
};

const getLast12Hours = () => {
  const now = new Date();
  const twelveHoursAgo = new Date(now.getTime() - 12 * 60 * 60 * 1000);

  return {
    rangeFrom: timestampFromDate(twelveHoursAgo),
    rangeTo: timestampFromDate(now),
  };
};

const getLastMinute = () => {
  const now = new Date();
  const oneMinuteAgo = new Date(now.getTime() - 60 * 1000);
  return {
    rangeFrom: timestampFromDate(oneMinuteAgo),
    rangeTo: timestampFromDate(now),
  };
};

export const LIST_METRIC_QUERY_KEY = 'listMetrics';

export const CHART_METRIC_QUERY_KEY = 'chartMetric';

export const LAST_MINUTE_CHART_METRIC_QUERY_KEY = 'lastMinuteChartMetric';

export const SYSTEM_MODE_METRIC_QUERY_KEY = 'systemMode';

export const listMetricsQuery = ({
  targets,
  aggregation = ListMetricsRequest_AggregationType.MOST_RECENT_ONLY,
  simulation,
}: {
  targets: ListMetricsRequest_Target[];
  aggregation?: ListMetricsRequest_AggregationType;
  simulation?: SimulationModel;
}) =>
  queryOptions({
    queryKey: [
      LIST_METRIC_QUERY_KEY,
      {
        types: targets,
        aggregation,
        simulationId: simulation?.id,
      },
    ],
    queryFn: async () => {
      if (targets.length === 0) {
        return new MetricsModel();
      }

      const metricsResponse = simulation
        ? await listSimulationMetrics({
            simulationId: simulation.id,
            targets:
              targets as unknown as ListSimulationMetricsRequest_Target[],
            aggregation:
              aggregation as unknown as ListSimulationMetricsRequest_AggregationType,
            rangeFrom: simulation.weekBeforeEnd,
            rangeTo: simulation.timeTo,
          })
        : await listMetrics({
            targets,
            aggregation,
            rangeFrom: getLastWeekRange().rangeFrom,
            rangeTo: getLastWeekRange().rangeTo,
          });

      return new MetricsModel(metricsResponse);
    },
    staleTime: 60 * 5_000,
  });

export const chartMetricQuery = ({
  target,
  simulation,
}: {
  target: ListMetricsRequest_Target;
  simulation?: SimulationModel;
}) =>
  queryOptions({
    queryKey: [CHART_METRIC_QUERY_KEY, target, simulation?.id],
    queryFn: async () => {
      const metricsResponse = simulation
        ? await listSimulationMetrics({
            simulationId: simulation.id,
            targets: [target as unknown as ListSimulationMetricsRequest_Target],
            rangeFrom: simulation.timeFrom,
            rangeTo: simulation.timeTo,
          })
        : await listMetrics({
            targets: [target],
            rangeFrom: getLastFiveYearsRange().rangeFrom,
            rangeTo: getLastFiveYearsRange().rangeTo,
          });
      return new ChartMetricModel(metricsResponse.metrics[0]);
    },
    staleTime: 60 * 5_000,
  });

export const lastMinuteChartMetrics = ({
  targets,
}: {
  targets: ListMetricsRequest_Target[];
}) =>
  queryOptions({
    queryKey: [LAST_MINUTE_CHART_METRIC_QUERY_KEY, targets],
    queryFn: async () => {
      const { rangeFrom, rangeTo } = getLastMinute();

      return await listMetrics({
        targets,
        rangeFrom,
        rangeTo,
      });
    },
    staleTime: 60 * 5_000,
  });

export const systemModeMetricQuery = ({
  target,
  simulation,
}: {
  target: ListMetricsRequest_Target;
  simulation?: SimulationModel;
}) =>
  queryOptions({
    queryKey: [SYSTEM_MODE_METRIC_QUERY_KEY, target],
    queryFn: async () => {
      const metricsResponse = simulation
        ? await listSimulationMetrics({
            simulationId: simulation.id,
            rangeFrom: simulation.timeFrom,
            rangeTo: simulation.timeTo,
            targets: [target as unknown as ListSimulationMetricsRequest_Target],
          })
        : await listMetrics({
            targets: [target],
            rangeFrom: getLastFiveYearsRange().rangeFrom,
            rangeTo: getLastFiveYearsRange().rangeTo,
          });
      return new SystemModeMetricModel(metricsResponse.metrics[0]);
    },
  });

export const waterLevelsMetricsQuery = ({
  reservoirNames,
  simulation,
  currentOnly,
}: {
  reservoirNames: string[];
  simulation?: SimulationModel;
  currentOnly?: boolean;
}) =>
  queryOptions({
    queryKey: [
      'waterLevelMetrics',
      reservoirNames,
      currentOnly,
      simulation?.id,
    ],
    queryFn: async () => {
      const targets = reservoirNames.map((resourceName) =>
        create(ListMetricsRequest_TargetSchema, {
          resourceName,
          types: [
            MetricType.WATER_HEIGHT_MM,
            MetricType.CALCULATED_WATER_PERCENTAGE,
            MetricType.CALCULATED_WATER_VOLUME_M3,
          ],
        }),
      );

      let waterLevelMetrics;

      if (targets.length === 0) {
        waterLevelMetrics = create(ListMetricsResponseSchema);
      } else if (simulation) {
        waterLevelMetrics = await listSimulationMetrics({
          aggregation:
            ListSimulationMetricsRequest_AggregationType.MOST_RECENT_ONLY,
          simulationId: simulation.id,
          targets: targets as unknown as ListSimulationMetricsRequest_Target[],
          rangeFrom: simulation.twelveHoursBeforeEnd,
          rangeTo: simulation.timeTo,
        });
      } else {
        const { rangeFrom, rangeTo } = currentOnly
          ? getLast12Hours()
          : getLastWeekRange();

        waterLevelMetrics = await listMetrics({
          targets,
          rangeFrom,
          rangeTo,
          aggregation: ListMetricsRequest_AggregationType.MOST_RECENT_ONLY,
        });
      }
      return new WaterLevelsModel(waterLevelMetrics);
    },
  });
